import * as Sentry from '@sentry/vue'

import type { Plugin } from 'vue'

const externalBrowserErrors = new Set([
  "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
  'Invalid call to runtime.sendMessage(). Tab not found.',
])

function beforeSendSentryEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const exceptions = event.exception?.values ?? []
  const exceptionValues = exceptions.map(({ value }) => value ?? '')
  const frames = exceptions.flatMap(({ stacktrace }) => stacktrace?.frames ?? [])

  if (
    exceptionValues.some((value) => externalBrowserErrors.has(value)) ||
    frames.some(({ function: functionName }) => functionName === 'addEL_hook')
  ) {
    return null
  }

  // Chrome and the Google app on iOS inject a script whose minified function names change with
  // every browser release. Its frames point at the page document, while our code only runs from
  // script files, because the layout ships no inline scripts.
  if (
    exceptionValues.includes('Maximum call stack size exceeded.') &&
    frames.length > 0 &&
    frames.every(({ filename }) => filename !== undefined && !/\.js([?#]|$)/.test(filename))
  ) {
    event.fingerprint = ['injected-script-call-stack-overflow']
  }

  return event
}

const sentryVuePlugin: Plugin = {
  install: (app) => {
    Sentry.init({
      enabled: import.meta.env.VITE_SENTRY_VUE_ENABLED === 'true',
      app,
      dsn: import.meta.env.VITE_SENTRY_VUE_DSN ?? '',
      tracesSampleRate: Number.parseFloat(
        import.meta.env.VITE_SENTRY_VUE_TRACES_SAMPLE_RATE ?? '0.0',
      ),

      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'failed',
      beforeSend: beforeSendSentryEvent,

      replaysSessionSampleRate: Number.parseFloat(
        import.meta.env.VITE_SENTRY_VUE_REPLAYS_SESSION_SAMPLE_RATE ?? '0.0',
      ),
      replaysOnErrorSampleRate: Number.parseFloat(
        import.meta.env.VITE_SENTRY_VUE_REPLAYS_ON_ERROR_SAMPLE_RATE ?? '1.0',
      ),

      integrations: [
        Sentry.vueIntegration({
          tracingOptions: { trackComponents: false },
        }),
        Sentry.replayIntegration(),
        Sentry.browserTracingIntegration(),
        Sentry.feedbackIntegration({
          colorScheme: 'system',
          autoInject: false,
          showBranding: false,
          useSentryUser: {
            email: 'email',
            name: 'username',
          },
          formTitle: 'Bug melden',
          submitButtonLabel: 'Absenden',
          cancelButtonLabel: 'Abbrechen',
          nameLabel: 'Name',
          namePlaceholder: 'Dein Name',
          emailLabel: 'E-Mail',
          emailPlaceholder: 'deine@email.de',
          messageLabel: 'Beschreibung',
          messagePlaceholder: 'Was ist passiert? Was hast du erwartet?',
          successMessageText: 'Danke für dein Feedback!',
          isRequiredLabel: '(Pflichtfeld)',
          addScreenshotButtonLabel: 'Screenshot hinzufügen',
          removeScreenshotButtonLabel: 'Screenshot entfernen',
        }),
      ],
    })
  },
}

export default sentryVuePlugin
